import logError from 'src/data/services/logError';
import React, { useState, useEffect } from 'react';
import { View, ScrollView, RefreshControl } from 'react-native';
import _ from 'lodash';
import NavBar from 'src/presentation/components/organisms/NavBar';
import Loader from 'src/presentation/components/atoms/Loader';
import Card from 'src/presentation/components/atoms/Card';
import GalleryGrid from 'src/presentation/components/organisms/GalleryGrid';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useGalleryStore } from 'src/domain/stores/useGalleryStore';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { GalleryStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import Colors from 'src/core/utils/Colors';
import Translations from 'src/core/utils/Translations';
import Constants from 'src/core/utils/Constants';
import type { ApiGallery } from 'src/core/models/api/ApiGallery';

function RootGalleriesScreen() {
	const navigation = useNavigation<NativeStackNavigationProp<GalleryStackParamList>>();
	const eventStore = useEventStore();
	const galleryStore = useGalleryStore();

	const [filtered, setFiltered] = useState(false);
	const [filteredResults, setFilteredResults] = useState<ApiGallery[] | false>(false);
	const [refreshing, setRefreshing] = useState(false);
	// True until the first gallery fetch settles.
	const [initialLoad, setInitialLoad] = useState(true);

	const fetchGalleries = () => {
		if (eventStore.eventUuids) {
			galleryStore
				.fetchGalleries(eventStore.eventUuids)
				.catch(logError)
				.finally(() => setInitialLoad(false));
		} else {
			setInitialLoad(false);
		}
	};

	const handleRefresh = () => {
		if (eventStore.eventUuids) {
			setRefreshing(true);
			galleryStore
				.fetchGalleries(eventStore.eventUuids, true)
				.then(() => {
					setRefreshing(false);
				})
				.catch(() => {
					setRefreshing(false);
				});
		}
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		fetchGalleries();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		fetchGalleries();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eventStore.eventUuids]);

	const searchEvent = (term: string) => {
		term.length > 0 ? setFiltered(true) : setFiltered(false);

		const results = galleryStore.allGalleries.filter((gallery) =>
			gallery.event_name.toLowerCase().includes(term.toLowerCase()),
		);

		setFilteredResults(results.length > 0 ? results : false);
	};

	const galleriesForGrid: ApiGallery[] = filtered
		? filteredResults === false
			? []
			: filteredResults
		: galleryStore.allGalleries;

	// Only block with a loader while there is genuinely nothing to show yet; once galleries are cached a
	// revisit/refresh keeps the tiles visible (the thumbnails fade in on their own).
	const showLoader = (galleryStore.isLoading || initialLoad) && galleryStore.allGalleries.length === 0;

	return (
		<View style={{ flex: 1 }}>
			<NavBar
				backBtn={false}
				bgcolor={Colors.primary}
				centerTitle={true}
				color={Colors.black}
				editBtn={false}
				iosPlaceholder="Zoeken in foto's"
				searchBtn={true}
				title={Translations.titlePhotos}
				onSearchTermChange={(term: string) => searchEvent(term)}
			/>

			{showLoader ? (
				<Loader />
			) : (
				<ScrollView
					contentContainerStyle={{ flexGrow: 1 }}
					refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
					style={{ flex: 1 }}
				>
					<Card>
						<GalleryGrid
							filtered={filtered}
							galleries={galleriesForGrid}
							onGalleryTilePress={(data: ApiGallery) => {
								navigation.navigate(Constants.screenRootGalleryHolder, data);
							}}
						/>
					</Card>
				</ScrollView>
			)}
		</View>
	);
}

export default RootGalleriesScreen;
